// client/src/coms/components/ComsMobileShell.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isMobileViewport } from './comslayout_parts/layoutUtils';
import { resolveSwipe, swipeStartInfo } from './mobile/gestureRules';

const DEFAULT_GESTURES = { swipeRight: 'servers', swipeLeft: 'members', swipeUp: 'none', swipeDown: 'none' };
const DRAWER_GAP = 48;

function clamp(n, min, max) {
  if (n < min) return min;
  if (n > max) return max;
  return n;
}

function readViewportMetrics() {
  try {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    const iw = typeof window !== 'undefined' ? window.innerWidth || 360 : 360;
    const ih = typeof window !== 'undefined' ? window.innerHeight || 640 : 640;

    if (!vv) {
      return {
        width: iw,
        height: ih,
        offsetTop: 0,
        offsetLeft: 0,
        pageTop: 0,
        pageLeft: 0,
      };
    }

    return {
      width: Math.round(vv.width || iw),
      height: Math.round(vv.height || ih),
      layoutHeight: ih,
      offsetTop: Math.round(vv.offsetTop || 0),
      offsetLeft: Math.round(vv.offsetLeft || 0),
      pageTop: Math.round(vv.pageTop || 0),
      pageLeft: Math.round(vv.pageLeft || 0),
    };
  } catch {
    return {
      width: 360,
      height: 640,
      offsetTop: 0,
      offsetLeft: 0,
      pageTop: 0,
      pageLeft: 0,
    };
  }
}

function isInteractiveElement(target) {
  try {
    const el = target && target.closest
      ? target.closest('input, textarea, select, option, [contenteditable="true"], [data-coms-no-swipe="true"]')
      : null;
    return !!el;
  } catch {
    return false;
  }
}

export function useComsMobileShellLayout() {
  const [mobile, setMobile] = useState(() => isMobileViewport());
  const [leftOpen, setLeftOpen] = useState(false);
  const [rightOpen, setRightOpen] = useState(false);
  const [viewport, setViewport] = useState(() => readViewportMetrics());
  const closePanelsRef = useRef(() => {});

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    function onResize() {
      setMobile(isMobileViewport());
      setViewport(readViewportMetrics());
    }

    const vv = window.visualViewport || null;

    onResize();

    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    if (vv) {
      vv.addEventListener('resize', onResize);
      vv.addEventListener('scroll', onResize);
    }

    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
      if (vv) {
        vv.removeEventListener('resize', onResize);
        vv.removeEventListener('scroll', onResize);
      }
    };
  }, []);

  useEffect(() => {
    if (!mobile) {
      setLeftOpen(false);
      setRightOpen(false);
    }
  }, [mobile]);

  useEffect(() => {
    closePanelsRef.current = () => {
      setLeftOpen(false);
      setRightOpen(false);
    };
  }, []);

  return {
    mobile,
    leftOpen,
    setLeftOpen,
    rightOpen,
    setRightOpen,
    viewport,
    closePanelsRef,
  };
}

export default function ComsMobileShell({
  mobile,
  sidebarWidth,
  closePanelsExternalRef,
  leftOpen,
  setLeftOpen,
  rightOpen,
  setRightOpen,
  renderRail,
  renderSidebar,
  renderRightPanel,
  onStartResizeSidebar,
  showDesktopSidebar = true,
  enableMainSwipeOpen = true,
  // Phones: what each swipe does (Mobile Layout → Gestures). Swiping right to Servers and left to Members drag the
  // drawers open; every other action runs through onGesture when the swipe ends.
  gestures = DEFAULT_GESTURES,
  onGesture,
  // Personalisation (desktop): extra shell classes, the collapsed-sidebar handle, layout-editing tag,
  // keyboard resizing and Ctrl/middle-click capture for "open in new tab".
  shellClassName = '',
  sidebarResizing = false,
  sidebarHidden = false,
  onShowSidebar,
  sidebarTag = null,
  onSidebarClickCapture,
  onResizeSidebarKey,
  children,
}) {
  const [viewport, setViewport] = useState(() => readViewportMetrics());
  const [drag, setDrag] = useState(null);
  const dragRef = useRef(null);
  const shellRef = useRef(null);
  const leftPanelRef = useRef(null);
  const rightPanelRef = useRef(null);

  const startGesture = useCallback((clientX, clientY, pointerId, source, target) => {
    if (!mobile) return;
    if (!enableMainSwipeOpen && !leftOpen && !rightOpen) return;
    if (leftOpen || rightOpen) {
      if (isInteractiveElement(target)) return;
    } else if (!swipeStartInfo(target, clientX, window.innerWidth, shellRef.current).horizontal) {
      // Edges (system Back), text fields, selections, canvases and sideways-scrolling rows keep their own swipes.
      return;
    }

    const startX = Number(clientX || 0);
    const startY = Number(clientY || 0);
    const base = {
      active: false,
      pointerId,
      source,
      startX,
      startY,
      lastX: startX,
      lastY: startY,
      dx: 0,
      dy: 0,
      side: null,
      opening: false,
      startedAt: Date.now(),
    };

    dragRef.current = base;
  }, [enableMainSwipeOpen, leftOpen, mobile, rightOpen]);

  // Swipes that do something other than drag a drawer (Chats, the switcher, the menu, previous/next conversation)
  // and the vertical swipes on the bottom navigation and the header are read when the finger lifts.
  const gesturesRef = useRef(gestures);
  gesturesRef.current = gestures;
  const onGestureRef = useRef(onGesture);
  onGestureRef.current = onGesture;
  useEffect(() => {
    const root = shellRef.current;
    if (!mobile || !enableMainSwipeOpen || !root) return undefined;
    let start = null;
    const onStart = (e) => {
      start = null;
      if (leftOpen || rightOpen || !e.touches || e.touches.length !== 1) return;
      const t = e.touches[0];
      const info = swipeStartInfo(e.target, t.clientX, window.innerWidth, root);
      if (!info.horizontal && !info.zone) return;
      start = { x: t.clientX, y: t.clientY, at: Date.now(), ...info };
    };
    const onEnd = (e) => {
      const s = start;
      start = null;
      const t = e.changedTouches && e.changedTouches[0];
      if (!s || !t || Date.now() - s.at > 900) return;
      const dx = t.clientX - s.x;
      const dy = t.clientY - s.y;
      const dir = resolveSwipe({ dx, dy, horizontal: s.horizontal, zone: s.zone });
      if (!dir) return;
      const action = gesturesRef.current[dir] || 'none';
      // These two are the drawer drags handled above.
      if ((dir === 'swipeRight' && action === 'servers') || (dir === 'swipeLeft' && action === 'members')) return;
      if (action !== 'none' && typeof onGestureRef.current === 'function') onGestureRef.current(action, dir);
    };
    const onCancel = () => {
      start = null;
    };
    root.addEventListener('touchstart', onStart, { capture: true, passive: true });
    root.addEventListener('touchend', onEnd, { capture: true, passive: true });
    root.addEventListener('touchcancel', onCancel, { capture: true, passive: true });
    return () => {
      root.removeEventListener('touchstart', onStart, { capture: true });
      root.removeEventListener('touchend', onEnd, { capture: true });
      root.removeEventListener('touchcancel', onCancel, { capture: true });
    };
  }, [mobile, enableMainSwipeOpen, leftOpen, rightOpen]);

  const closePanels = useCallback(() => {
    setLeftOpen(false);
    setRightOpen(false);
    setDrag(null);
    dragRef.current = null;
  }, [setLeftOpen, setRightOpen]);

  const openLeftPanel = useCallback(() => {
    setRightOpen(false);
    setLeftOpen(true);
    setDrag(null);
    dragRef.current = null;
  }, [setLeftOpen, setRightOpen]);

  const closeLeftPanel = useCallback(() => {
    setLeftOpen(false);
    setDrag(null);
    dragRef.current = null;
  }, [setLeftOpen]);

  const toggleLeftPanel = useCallback(() => {
    setRightOpen(false);
    setLeftOpen((v) => !v);
    setDrag(null);
    dragRef.current = null;
  }, [setLeftOpen, setRightOpen]);

  const openRightPanel = useCallback(() => {
    setLeftOpen(false);
    setRightOpen(true);
    setDrag(null);
    dragRef.current = null;
  }, [setLeftOpen, setRightOpen]);

  const closeRightPanel = useCallback(() => {
    setRightOpen(false);
    setDrag(null);
    dragRef.current = null;
  }, [setRightOpen]);

  const toggleRightPanel = useCallback(() => {
    setLeftOpen(false);
    setRightOpen((v) => !v);
    setDrag(null);
    dragRef.current = null;
  }, [setLeftOpen, setRightOpen]);

  useEffect(() => {
    if (!closePanelsExternalRef) return;
    closePanelsExternalRef.current = closePanels;
  }, [closePanelsExternalRef, closePanels]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    function onViewportChange() {
      setViewport(readViewportMetrics());
    }

    const vv = window.visualViewport || null;

    onViewportChange();

    window.addEventListener('resize', onViewportChange);
    window.addEventListener('orientationchange', onViewportChange);
    if (vv) {
      vv.addEventListener('resize', onViewportChange);
      vv.addEventListener('scroll', onViewportChange);
    }

    return () => {
      window.removeEventListener('resize', onViewportChange);
      window.removeEventListener('orientationchange', onViewportChange);
      if (vv) {
        vv.removeEventListener('resize', onViewportChange);
        vv.removeEventListener('scroll', onViewportChange);
      }
    };
  }, []);

  useEffect(() => {
    try {
      if (typeof document === 'undefined') return;
      const b = document.body;
      if (!b) return;
      if (mobile && (leftOpen || rightOpen || drag)) b.classList.add('coms-no-scroll');
      else b.classList.remove('coms-no-scroll');
    } catch {}
  }, [mobile, leftOpen, rightOpen, drag]);

  useEffect(() => {
    try {
      if (typeof document === 'undefined') return;
      const root = document.documentElement;
      if (!root) return;
      root.style.setProperty('--coms-vh', `${viewport.height}px`);
      root.style.setProperty('--coms-vw', `${viewport.width}px`);
      root.style.setProperty('--coms-vv-top', `${viewport.offsetTop}px`);
      root.style.setProperty('--coms-vv-left', `${viewport.offsetLeft}px`);
    } catch {}
  }, [viewport]);

  useEffect(() => {
    if (!mobile) return undefined;

    function onKeyDown(e) {
      if (e && e.key === 'Escape') closePanels();
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [mobile, closePanels]);


  const viewportWidth = mobile ? viewport.width : 0;
  const viewportHeight = mobile ? viewport.height : 0;
  const viewportTop = mobile ? viewport.offsetTop : 0;
  const viewportLeft = mobile ? viewport.offsetLeft : 0;

  // Drawers leave a strip of the (dimmed) conversation showing: tapping it closes the drawer, whatever navigation
  // layout is in use (with menus or gestures there may be no other button on screen).
  const leftPanelW = useMemo(() => {
    if (!mobile) return sidebarWidth;
    const vw = viewportWidth || 360;
    return Math.max(1, Math.min(vw - DRAWER_GAP, 480));
  }, [mobile, sidebarWidth, viewportWidth]);

  const rightPanelW = useMemo(() => {
    if (!mobile) return 240;
    const vw = viewportWidth || 360;
    return Math.max(1, Math.min(vw - DRAWER_GAP, 420));
  }, [mobile, viewportWidth]);

  useEffect(() => {
    if (!mobile || !leftOpen) return undefined;

    const panel = leftPanelRef.current;
    if (!panel) return undefined;

    let swipe = null;

    function getTouch(e) {
      return e && e.touches && e.touches.length ? e.touches[0] : null;
    }

    function onNativeTouchStart(e) {
      if (!mobile || !leftOpen) return;
      if (isInteractiveElement(e && e.target)) return;

      const t = getTouch(e);
      if (!t) return;

      swipe = {
        active: false,
        startX: Number(t.clientX || 0),
        startY: Number(t.clientY || 0),
        lastX: Number(t.clientX || 0),
        lastY: Number(t.clientY || 0),
        startedAt: Date.now(),
      };
    }

    function onNativeTouchMove(e) {
      if (!swipe) return;

      const t = getTouch(e);
      if (!t) return;

      const x = Number(t.clientX || 0);
      const y = Number(t.clientY || 0);
      const dx = x - swipe.startX;
      const dy = y - swipe.startY;
      const adx = Math.abs(dx);
      const ady = Math.abs(dy);

      swipe.lastX = x;
      swipe.lastY = y;

      if (!swipe.active) {
        if (ady > 16 && ady > adx * 1.1) {
          swipe = null;
          return;
        }

        if (dx > -18 || adx < ady * 1.2) return;
        swipe.active = true;
      }

      if (swipe.active) {
        try {
          if (typeof e.preventDefault === 'function') e.preventDefault();
        } catch {}
      }
    }

    function onNativeTouchEnd() {
      if (!swipe) return;

      const dx = swipe.lastX - swipe.startX;
      const dy = swipe.lastY - swipe.startY;
      const travel = Math.abs(dx);
      const elapsed = Math.max(1, Date.now() - (swipe.startedAt || Date.now()));
      const velocity = travel / elapsed;
      const shouldClose = dx < 0 && travel > Math.max(46, leftPanelW * 0.12) && travel > Math.abs(dy) * 1.15;

      swipe = null;

      if (shouldClose || (dx < -32 && velocity > 0.45)) {
        closeLeftPanel();
      }
    }

    function onNativeTouchCancel() {
      swipe = null;
    }

    panel.addEventListener('touchstart', onNativeTouchStart, { capture: true, passive: true });
    panel.addEventListener('touchmove', onNativeTouchMove, { capture: true, passive: false });
    panel.addEventListener('touchend', onNativeTouchEnd, { capture: true, passive: true });
    panel.addEventListener('touchcancel', onNativeTouchCancel, { capture: true, passive: true });

    return () => {
      panel.removeEventListener('touchstart', onNativeTouchStart, { capture: true });
      panel.removeEventListener('touchmove', onNativeTouchMove, { capture: true });
      panel.removeEventListener('touchend', onNativeTouchEnd, { capture: true });
      panel.removeEventListener('touchcancel', onNativeTouchCancel, { capture: true });
    };
  }, [closeLeftPanel, leftOpen, leftPanelW, mobile]);


  useEffect(() => {
    if (!mobile || !rightOpen) return undefined;

    const panel = rightPanelRef.current;
    if (!panel) return undefined;

    let swipe = null;

    function getTouch(e) {
      return e && e.touches && e.touches.length ? e.touches[0] : null;
    }

    function onNativeTouchStart(e) {
      if (!mobile || !rightOpen) return;
      if (isInteractiveElement(e && e.target)) return;

      const t = getTouch(e);
      if (!t) return;

      swipe = {
        active: false,
        startX: Number(t.clientX || 0),
        startY: Number(t.clientY || 0),
        lastX: Number(t.clientX || 0),
        lastY: Number(t.clientY || 0),
        startedAt: Date.now(),
      };
    }

    function onNativeTouchMove(e) {
      if (!swipe) return;

      const t = getTouch(e);
      if (!t) return;

      const x = Number(t.clientX || 0);
      const y = Number(t.clientY || 0);
      const dx = x - swipe.startX;
      const dy = y - swipe.startY;
      const adx = Math.abs(dx);
      const ady = Math.abs(dy);

      swipe.lastX = x;
      swipe.lastY = y;

      if (!swipe.active) {
        if (ady > 16 && ady > adx * 1.1) {
          swipe = null;
          return;
        }

        if (dx < 18 || adx < ady * 1.2) return;
        swipe.active = true;
      }

      if (swipe.active) {
        try {
          if (typeof e.preventDefault === 'function') e.preventDefault();
        } catch {}
      }
    }

    function onNativeTouchEnd() {
      if (!swipe) return;

      const dx = swipe.lastX - swipe.startX;
      const dy = swipe.lastY - swipe.startY;
      const travel = Math.abs(dx);
      const elapsed = Math.max(1, Date.now() - (swipe.startedAt || Date.now()));
      const velocity = travel / elapsed;
      const shouldClose = dx > 0 && travel > Math.max(46, rightPanelW * 0.12) && travel > Math.abs(dy) * 1.15;

      swipe = null;

      if (shouldClose || (dx > 32 && velocity > 0.45)) {
        closeRightPanel();
      }
    }

    function onNativeTouchCancel() {
      swipe = null;
    }

    panel.addEventListener('touchstart', onNativeTouchStart, { capture: true, passive: true });
    panel.addEventListener('touchmove', onNativeTouchMove, { capture: true, passive: false });
    panel.addEventListener('touchend', onNativeTouchEnd, { capture: true, passive: true });
    panel.addEventListener('touchcancel', onNativeTouchCancel, { capture: true, passive: true });

    return () => {
      panel.removeEventListener('touchstart', onNativeTouchStart, { capture: true });
      panel.removeEventListener('touchmove', onNativeTouchMove, { capture: true });
      panel.removeEventListener('touchend', onNativeTouchEnd, { capture: true });
      panel.removeEventListener('touchcancel', onNativeTouchCancel, { capture: true });
    };
  }, [closeRightPanel, mobile, rightOpen, rightPanelW]);

  const updateDrag = useCallback((next) => {
    dragRef.current = next;
    setDrag(next);
  }, []);

  const finishDrag = useCallback(() => {
    const d = dragRef.current;
    dragRef.current = null;
    setDrag(null);
    if (!d || !d.active || !d.side) return;

    const width = d.side === 'left' ? leftPanelW : rightPanelW;
    const travel = Math.abs(d.dx || 0);
    const elapsed = Math.max(1, Date.now() - (d.startedAt || Date.now()));
    const velocity = travel / elapsed;
    const shouldCommit = travel > Math.max(84, width * 0.32) || velocity > 0.7;

    if (d.side === 'left') {
      if (d.opening) {
        if (shouldCommit) openLeftPanel();
        else closeLeftPanel();
      } else if (shouldCommit) {
        closeLeftPanel();
      } else {
        openLeftPanel();
      }
    }

    if (d.side === 'right') {
      if (d.opening) {
        if (shouldCommit) openRightPanel();
        else closeRightPanel();
      } else if (shouldCommit) {
        closeRightPanel();
      } else {
        openRightPanel();
      }
    }
  }, [closeLeftPanel, closeRightPanel, leftPanelW, openLeftPanel, openRightPanel, rightPanelW]);

  const onPointerDown = useCallback((e) => {
    if (!mobile) return;
    if (!e || (e.pointerType && e.pointerType !== 'touch' && e.pointerType !== 'pen')) return;

    const existing = dragRef.current;
    if (existing && existing.source === 'touch' && Date.now() - (existing.startedAt || 0) < 500) return;

    startGesture(e.clientX, e.clientY, e.pointerId, 'pointer', e.target);
  }, [mobile, startGesture]);

  const processGestureMove = useCallback((clientX, clientY, eventLike) => {
    const d = dragRef.current;
    if (!mobile || !d) return;

    const x = Number(clientX || 0);
    const y = Number(clientY || 0);
    const dx = x - d.startX;
    const dy = y - d.startY;
    const adx = Math.abs(dx);
    const ady = Math.abs(dy);

    if (!d.active) {
      if (ady > 14 && ady > adx * 1.1) {
        dragRef.current = null;
        return;
      }

      if (adx < 14 || adx < ady * 1.2) {
        dragRef.current = { ...d, lastX: x, lastY: y, dx, dy };
        return;
      }

      let side = null;
      let opening = false;

      if (leftOpen) {
        if (dx < 0) side = 'left';
      } else if (rightOpen) {
        if (dx > 0) side = 'right';
      } else if (dx > 0 && gestures.swipeRight === 'servers') {
        side = 'left';
        opening = true;
      } else if (dx < 0 && gestures.swipeLeft === 'members') {
        side = 'right';
        opening = true;
      }

      if (!side) {
        dragRef.current = null;
        return;
      }

      const next = {
        ...d,
        active: true,
        side,
        opening,
        lastX: x,
        lastY: y,
        dx,
        dy,
      };
      updateDrag(next);
      try {
        if (eventLike && eventLike.currentTarget && eventLike.currentTarget.setPointerCapture) {
          eventLike.currentTarget.setPointerCapture(eventLike.pointerId);
        }
      } catch {}
      try {
        if (eventLike && typeof eventLike.preventDefault === 'function') eventLike.preventDefault();
      } catch {}
      return;
    }

    const next = {
      ...d,
      lastX: x,
      lastY: y,
      dx,
      dy,
    };
    updateDrag(next);
    try {
      if (eventLike && typeof eventLike.preventDefault === 'function') eventLike.preventDefault();
    } catch {}
  }, [gestures.swipeLeft, gestures.swipeRight, leftOpen, mobile, rightOpen, updateDrag]);

  const onPointerMove = useCallback((e) => {
    const d = dragRef.current;
    if (!mobile || !d) return;
    if (d.source === 'touch') return;
    if (d.pointerId != null && e.pointerId != null && d.pointerId !== e.pointerId) return;
    processGestureMove(e.clientX, e.clientY, e);
  }, [mobile, processGestureMove]);

  const onTouchStart = useCallback((e) => {
    if (!mobile) return;
    const t = e && e.touches && e.touches.length ? e.touches[0] : null;
    if (!t) return;
    startGesture(t.clientX, t.clientY, 'touch', 'touch', e.target);
  }, [mobile, startGesture]);

  const onTouchMove = useCallback((e) => {
    const d = dragRef.current;
    if (!mobile || !d || d.source !== 'touch') return;
    const t = e && e.touches && e.touches.length ? e.touches[0] : null;
    if (!t) return;
    processGestureMove(t.clientX, t.clientY, e);
  }, [mobile, processGestureMove]);

  const onPointerCancel = useCallback(() => {
    dragRef.current = null;
    setDrag(null);
  }, []);

  const membersDrawerOpen = !!rightOpen;
  const draggingLeft = drag && drag.active && drag.side === 'left';
  const draggingRight = drag && drag.active && drag.side === 'right';

  const leftProgress = (() => {
    if (!mobile) return leftOpen ? 1 : 0;
    if (draggingLeft) {
      if (drag.opening) return clamp((drag.dx || 0) / Math.max(1, leftPanelW), 0, 1);
      return clamp(1 + (drag.dx || 0) / Math.max(1, leftPanelW), 0, 1);
    }
    return leftOpen ? 1 : 0;
  })();

  const rightProgress = (() => {
    if (!mobile) return rightOpen ? 1 : 0;
    if (draggingRight) {
      if (drag.opening) return clamp(-(drag.dx || 0) / Math.max(1, rightPanelW), 0, 1);
      return clamp(1 - (drag.dx || 0) / Math.max(1, rightPanelW), 0, 1);
    }
    return rightOpen ? 1 : 0;
  })();

  const panelMoving = !!(draggingLeft || draggingRight);
  // The on-screen keyboard is up: the visible area is much shorter than the page. Navigation at the bottom steps
  // aside only then (not merely while the message box has focus, which can outlast the keyboard on Android).
  const keyboardOpen = mobile && !!viewport.layoutHeight && viewport.layoutHeight - viewport.height > 150;
  const scrimOpacity = clamp(Math.max(leftProgress, rightProgress) * 0.48, 0, 0.48);
  const scrimVisible = mobile && (leftProgress > 0.01 || rightProgress > 0.01);

  const rootClassName = [
    'coms-shell',
    mobile ? 'coms-shell-mobile' : '',
    leftOpen ? 'coms-left-open' : '',
    rightOpen ? 'coms-right-open' : '',
    panelMoving ? 'coms-panel-dragging' : '',
    keyboardOpen ? 'coms-kb-open' : '',
  ]
    .filter(Boolean)
    .join(' ');

  if (!mobile) {
    return (
      <div className={shellClassName ? `${rootClassName} ${shellClassName}` : rootClassName}>
        {(showDesktopSidebar || sidebarHidden) && typeof renderRail === 'function' ? renderRail({ mobile: false, closePanels }) : null}

        {showDesktopSidebar ? (
          <div
            className="coms-sidebar-wrap"
            data-vx-region="sidebar"
            style={{ width: sidebarWidth }}
            onClickCapture={onSidebarClickCapture}
            onAuxClickCapture={onSidebarClickCapture}
          >
            {sidebarTag}
            {typeof renderSidebar === 'function' ? renderSidebar({ mobile: false, closePanels }) : null}

            <div
              className={`coms-sidebar-resizer${sidebarResizing ? ' vx-resizing' : ''}`}
              onPointerDown={onStartResizeSidebar}
              onKeyDown={onResizeSidebarKey}
              title="Drag to resize (or use the arrow keys)"
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize channel list"
              aria-valuemin={260}
              aria-valuemax={560}
              aria-valuenow={Math.round(Number(sidebarWidth) || 0)}
              tabIndex={0}
              data-testid="sidebar-resizer"
              style={{ touchAction: 'none' }}
            />
          </div>
        ) : null}

        {sidebarHidden ? (
          <button type="button" className="vx-edge-handle" onClick={onShowSidebar} aria-label="Show channel list" title="Show channel list" data-testid="sidebar-show-handle">
            ›
          </button>
        ) : null}

        {typeof children === 'function'
          ? children({
              mobile: false,
              membersDrawerOpen: false,
              closePanels,
              openLeftPanel,
              closeLeftPanel,
              toggleLeftPanel,
              openRightPanel,
              closeRightPanel,
              toggleRightPanel,
              mobileMetrics: null,
            })
          : null}
      </div>
    );
  }

  const leftTransform = `translate3d(${Math.round((leftProgress - 1) * leftPanelW)}px,0,0)`;
  const rightTransform = `translate3d(${Math.round((1 - rightProgress) * rightPanelW)}px,0,0)`;

  return (
    <div
      ref={shellRef}
      className={rootClassName}
      onPointerDownCapture={onPointerDown}
      onPointerMoveCapture={onPointerMove}
      onPointerUpCapture={finishDrag}
      onPointerCancelCapture={onPointerCancel}
      onTouchStartCapture={onTouchStart}
      onTouchMoveCapture={onTouchMove}
      onTouchEndCapture={finishDrag}
      onTouchCancelCapture={onPointerCancel}
      style={{
        position: 'relative',
        width: '100%',
        height: `${viewportHeight}px`,
        minHeight: `${viewportHeight}px`,
        touchAction: 'pan-y',
        overflow: 'hidden',
      }}
    >
      {typeof children === 'function'
        ? children({
            mobile: true,
            membersDrawerOpen,
            closePanels,
            openLeftPanel,
            closeLeftPanel,
            toggleLeftPanel,
            openRightPanel,
            closeRightPanel,
            toggleRightPanel,
            mobileMetrics: {
              viewportWidth,
              viewportHeight,
              viewportTop,
              viewportLeft,
              leftToggleBarW: 0,
              rightToggleBarW: 0,
              leftPanelW,
              rightPanelW,
            },
          })
        : null}

      {scrimVisible ? (
        <button
          className="coms-scrim"
          onClick={closePanels}
          aria-label="Close panels"
          style={{ opacity: scrimOpacity }}
        />
      ) : null}

      <div
        ref={leftPanelRef}
        onPointerDownCapture={onPointerDown}
        onPointerMoveCapture={onPointerMove}
        onPointerUpCapture={finishDrag}
        onPointerCancelCapture={onPointerCancel}
        onTouchStartCapture={onTouchStart}
        onTouchMoveCapture={onTouchMove}
        onTouchEndCapture={finishDrag}
        onTouchCancelCapture={onPointerCancel}
        style={{
          position: 'fixed',
          left: viewportLeft,
          top: viewportTop,
          width: leftPanelW,
          // Stops above the bottom navigation bar, which stays usable while a drawer is open.
          height: `calc(${viewportHeight}px - var(--vx-mnav-h, 0px))`,
          maxWidth: `${viewportWidth}px`,
          borderRadius: 0,
          border: 'none',
          background: 'rgba(var(--vxs-1-rgb, 10,13,20), 0.992)',
          boxShadow: '8px 0 40px rgba(0,0,0,0.45)',
          zIndex: 1206,
          overflow: 'hidden',
          touchAction: 'pan-y',
          transition: panelMoving ? 'none' : 'transform 210ms cubic-bezier(0.2, 0.8, 0.2, 1)',
          pointerEvents: leftProgress > 0.01 ? 'auto' : 'none',
          transform: leftTransform,
          display: 'flex',
          flexDirection: 'column',
          willChange: 'transform',
        }}
      >
        <div
          style={{
            height: 14,
            flexShrink: 0,
            background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.02 * var(--vx-ov, 1)))',
          }}
        />

        <div
          style={{
            minWidth: 0,
            minHeight: 0,
            flex: 1,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'row',
          }}
        >
          {typeof renderRail === 'function' ? renderRail({ mobile: true, closePanels }) : null}

          <div
            style={{
              minWidth: 0,
              minHeight: 0,
              flex: 1,
              overflow: 'hidden',
              touchAction: 'pan-y',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div style={{ display: 'contents' }} onClickCapture={onSidebarClickCapture} onAuxClickCapture={onSidebarClickCapture}>
              {typeof renderSidebar === 'function' ? renderSidebar({ mobile: true, closePanels }) : null}
            </div>
          </div>
        </div>
      </div>

      <div
        ref={rightPanelRef}
        onPointerDownCapture={onPointerDown}
        onPointerMoveCapture={onPointerMove}
        onPointerUpCapture={finishDrag}
        onPointerCancelCapture={onPointerCancel}
        onTouchStartCapture={onTouchStart}
        onTouchMoveCapture={onTouchMove}
        onTouchEndCapture={finishDrag}
        onTouchCancelCapture={onPointerCancel}
        style={{
          position: 'fixed',
          right: viewportLeft,
          top: viewportTop,
          width: rightPanelW,
          height: `calc(${viewportHeight}px - var(--vx-mnav-h, 0px))`,
          maxWidth: `${viewportWidth}px`,
          borderRadius: 0,
          border: 'none',
          background: 'rgba(var(--vxs-1-rgb, 10,13,20), 0.992)',
          boxShadow: '-8px 0 40px rgba(0,0,0,0.45)',
          zIndex: 1207,
          overflow: 'hidden',
          touchAction: 'pan-y',
          transition: panelMoving ? 'none' : 'transform 210ms cubic-bezier(0.2, 0.8, 0.2, 1)',
          pointerEvents: rightProgress > 0.98 ? 'auto' : 'none',
          transform: rightTransform,
          display: 'flex',
          flexDirection: 'column',
          willChange: 'transform',
        }}
      >
        <div
          style={{
            height: 14,
            flexShrink: 0,
            background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.02 * var(--vx-ov, 1)))',
          }}
        />

        <div
          style={{
            minHeight: 0,
            flex: 1,
            overflow: 'hidden',
          }}
        >
          {typeof renderRightPanel === 'function' ? renderRightPanel() : null}
        </div>
      </div>
    </div>
  );
}
